import React, {
  ChangeEvent,
  FocusEvent,
  SyntheticEvent,
  useCallback,
  useState,
} from "react"
import { formatUnits } from "viem"

import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  normalizeDecimalNumberInput,
  trimBeforeDecimals,
  trimDecimals,
} from "#/shared/utils"

import AmountInput, { AmountInputProps } from "./AmountInput"

export type TokenAmountInputProps = Required<Pick<AmountInputProps, "token">> &
  Omit<AmountInputProps, "onChange" | "onBlur" | "value" | "balanceToken"> & {
    tokenDecimals: number
    onChange: (value: bigint) => void
    onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    value: bigint | undefined
    balanceToken: bigint
  }

export default function TokenAmountInput({
  balanceToken,
  token,
  tokenDecimals,
  onChange,
  onBlur,
  value,
  ...props
}: TokenAmountInputProps) {
  const [displayedValue, setDisplayedValue] = useState<string>(
    value ? bigIntToFloat(value, tokenDecimals, tokenDecimals) : "",
  )

  // Removes multiple dots and trims decimals when the value exceeds the maximum
  // precision of the given token (`tokenDecimals`).
  const normalizeValue = useCallback(
    (inputValue: string) => {
      const normalizedValue = normalizeDecimalNumberInput(inputValue)
      const trimmedDecimals = trimDecimals(normalizedValue, tokenDecimals)

      return trimBeforeDecimals(trimmedDecimals, tokenDecimals)
    },
    [tokenDecimals],
  )

  const handleMaxButton = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      event.preventDefault()
      setDisplayedValue(formatUnits(balanceToken, tokenDecimals))

      onChange(balanceToken)
    },
    [onChange, balanceToken, tokenDecimals],
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const targetValue = event.target.value
      const normalizedValue = normalizeValue(targetValue)

      setDisplayedValue(normalizedValue)

      onChange(floatToBigInt(normalizedValue, tokenDecimals))
    },
    [onChange, tokenDecimals, normalizeValue],
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value: targetValue } = event.target

      if (!targetValue || Number.isNaN(targetValue)) return

      const valueInTokenDecimalsPrecision = floatToBigInt(
        targetValue,
        tokenDecimals,
      )

      setDisplayedValue(
        formatUnits(valueInTokenDecimalsPrecision, tokenDecimals),
      )

      onChange(valueInTokenDecimalsPrecision)
      onBlur?.(event)
    },
    [onBlur, tokenDecimals, onChange],
  )

  return (
    <AmountInput
      token={token}
      balanceToken={bigIntToHumanReadableFormat(balanceToken, tokenDecimals)}
      onChange={handleChange}
      onBlur={handleBlur}
      onButtonMax={handleMaxButton}
      {...props}
      value={displayedValue}
    />
  )
}
